import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { Redirect } from 'react-router-dom'

// actions
import { useFundQuery, useTargetQuery, useDealQuery, usePipelineQuery } from './hook'
import { openModal, statusType } from '../../actions'

// components
import FundBanner from './banner/banner.component'
import FundOverview from './overview/overview.component'
import { SectionTab } from '../../components/shared'
import {
  ActivityGrid,
  ContactGrid,
  CurrentHoldingGrid,
  HistoricalHoldingGrid,
  PeerAnalysisGrid,
  Spinner,
  PageMessage
} from '../../components'

import { ENTITY_TYPE, THEMES, PAGE_MESSAGE, getLocalizedCurrency } from '../../utils'
import { get } from 'lodash'

// Data Ids
import { DATA_IDS } from '../../tests-e2e/pages/fund/fund.definition'

const {
  CONTACT_TAB,
  CONTACT_GRID,
  ACTIVITY_TAB,
  ACTIVITY_GRID,
  CURRENT_HOLDING_TAB,
  CURRENT_HOLDING_GRID,
  HISTORICAL_HOLDING_TAB,
  HISTORICAL_HOLDING_GRID,
  PEER_ANALYSIS_TAB,
  PEER_ANALYSIS_GRID
} = DATA_IDS

const dataIdPrefix = 'FundPage'

const propTypes = {}
const defaultProps = {}

const { FUND } = ENTITY_TYPE
const TABS = {
  CONTACT: 'contact',
  ACTIVITY: 'activity',
  CURRENT: 'current',
  HISTORICAL: 'historical',
  PEER: 'peer'
}

/**
 * Fund Profile Page
 */
function FundPage (props) {
  const { entity, isTargetSuccess, openModal } = props
  const { q4_entity_id: entityId, factset_fund_id, _id } = entity // TODO: remove when search migrated

  const { loading, error, data } = useFundQuery({
    variables: { id: entityId, currencyCode: getLocalizedCurrency() }
  })

  const fund = get(data, 'fund.items[0]', {})
  const { id, active } = fund

  const [getTarget, { data: target }] = useTargetQuery({
    variables: { entityId: id }
  })

  const { loading: isPipeline, data: stages } = usePipelineQuery()
  const [getDeals, { loading: isDeals, data: deal }] = useDealQuery({
    variables: { entityId: id }
  })

  const targetId = get(target, 'target.items[0].id', null)
  const deals = get(deal, 'deal.items', [])
  const pipeline = get(stages, 'pipeline.items', [])
  const lost = pipeline.find((item) => item.lost)
  const activeDeal = deals.find((deal) => deal.stage !== (lost && lost.id))

  useEffect(() => {
    if (!id) {
      return
    }

    getTarget()
    getDeals()
  }, [id, getTarget, getDeals])

  useEffect(() => {
    isTargetSuccess && getTarget()
  }, [isTargetSuccess, getTarget])

  const handleDealUpdate = () => {
    getTarget()
    getDeals()
  }

  /**
   * Render Fund Page
   */
  return (
    <>
      {(error || (data && !id)) && <Redirect to='/error/404' />}
      {loading && <Spinner mask theme={THEMES.DARK} />}
      {!!id && 
        (active
          ? <div className='fund-page'>
              <FundBanner
                dataIdPrefix={dataIdPrefix}
                // TODO remove factset_fund_id after search migrate
                // TODO remove _id after deal purchasing power migration
                fund={{ ...fund, factset_fund_id, _id }}
                targetId={targetId}
                hasActiveDeal={!!activeDeal}
                onDealSaveSuccess={handleDealUpdate}
                openModal={openModal}
              />
              <FundOverview
                dataIdPrefix={dataIdPrefix}
                fund={fund}
                targetId={targetId}
                hasActiveDeal={!!activeDeal}
                deals={{
                  isLoading: [isDeals, isPipeline].some((item) => !!item),
                  deals,
                  pipeline
                }}
                onDealSaveSuccess={handleDealUpdate}
              />

              <SectionTab
                theme={THEMES.RAIN}
                tabs={[
                  {
                    id: TABS.CONTACT,
                    label: 'Contacts',
                    dataId: {
                      label: CONTACT_TAB,
                      view: CONTACT_GRID
                    },
                    view: (
                      <ContactGrid
                        dataIdPrefix={dataIdPrefix}
                        entityId={id}
                        entityType={FUND}
                      />
                    )
                  },
                  {
                    id: TABS.ACTIVITY,
                    label: 'Activity',
                    dataId: {
                      label: ACTIVITY_TAB,
                      view: ACTIVITY_GRID
                    },
                    view: (
                      <ActivityGrid
                        dataIdPrefix={dataIdPrefix}
                        entityId={id}
                        entityType={FUND}
                        // TODO remove factset_fund_id after search migrate
                        entity={{ ...fund, factset_fund_id }}
                        showChildren
                      />
                    )
                  },
                  {
                    id: TABS.CURRENT,
                    label: 'Current Holdings',
                    dataId: {
                      label: CURRENT_HOLDING_TAB,
                      view: CURRENT_HOLDING_GRID
                    },
                    view: (
                      <CurrentHoldingGrid
                        dataIdPrefix={dataIdPrefix}
                        entityId={id}
                        entityType={FUND}
                      />
                    )
                  },
                  {
                    id: TABS.HISTORICAL,
                    label: 'Historical Holdings',
                    dataId: {
                      label: HISTORICAL_HOLDING_TAB,
                      view: HISTORICAL_HOLDING_GRID
                    },
                    view: (
                      <HistoricalHoldingGrid
                        dataIdPrefix={dataIdPrefix}
                        entityId={id}
                        entityType={FUND}
                      />
                    )
                  },
                  {
                    id: TABS.PEER,
                    label: 'Peer Analysis',
                    dataId: {
                      label: PEER_ANALYSIS_TAB,
                      view: PEER_ANALYSIS_GRID
                    },
                    view: (
                      <PeerAnalysisGrid
                        dataIdPrefix={dataIdPrefix}
                        entityId={id}
                        entityType={FUND}
                      />
                    )
                  }
                ]}
              />
            </div>
          : <PageMessage
              message={PAGE_MESSAGE.FUND_PAGE_INACTIVE}
            />
        )
      }
    </>
  )
}

FundPage.propTypes = propTypes
FundPage.defaultProps = defaultProps

const mapStateToProps = (state) => ({
  isTargetSuccess: get(state, 'targeting.target.status') === statusType.SUCCESS
})

const mapDispatchToProps = (dispatch) => ({
  openModal: bindActionCreators(openModal, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(FundPage)
